import { useCallback, useEffect, useRef, useState } from 'react';
import type { Row } from '../types';

const EXPORT_BATCH = 200;
/** How long the finished bar stays on screen. */
const DONE_LINGER_MS = 2000;

export interface ExportProgress { loaded: number; total: number }

interface Args {
  fetchPage: (skip: number, take: number) => Promise<Row[]>;
  totalCount: number;
}

interface Result {
  busy: boolean;
  progress: ExportProgress | null;
  exportAll: () => Promise<Row[] | null>;
  reset: () => void;
}

/**
 * Pulls every matching row by looping `listView` with a growing `skip`.
 * Reports progress after each batch so the UI can render a bar.
 */
export function useExportAll({ fetchPage, totalCount }: Args): Result {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const reset = useCallback(() => {
    clearTimeout(timer.current);
    setBusy(false);
    setProgress(null);
  }, []);

  const exportAll = useCallback(async (): Promise<Row[] | null> => {
    if (busy) return null;
    clearTimeout(timer.current);
    setBusy(true);
    setProgress({ loaded: 0, total: totalCount });

    const acc: Row[] = [];
    let skip = 0;
    try {
      while (skip < totalCount) {
        const take = Math.min(EXPORT_BATCH, totalCount - skip);
        const page = await fetchPage(skip, take);
        if (page.length === 0) break; // server stopped early — don't spin
        acc.push(...page);
        skip += page.length;
        setProgress({ loaded: acc.length, total: totalCount });
      }
      timer.current = setTimeout(() => setProgress(null), DONE_LINGER_MS);
      return acc;
    } catch (e) {
      console.error('[export] failed to fetch all rows:', e);
      setProgress(null);
      return null;
    } finally {
      setBusy(false);
    }
  }, [busy, fetchPage, totalCount]);

  return { busy, progress, exportAll, reset };
}